import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Lightbulb, Ruler, Palette } from 'lucide-react';
import { services, galleryImages } from '../data/mockData';
import { SiteVideo } from '../components/SiteVideo';

const Conception = () => {
  const conceptionImages = galleryImages.filter(img => img.category === 'conception');
  
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-orange-50 via-white to-orange-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texte à gauche */}
            <div>
              <div className="inline-block mb-6 px-4 py-2 bg-orange-100 rounded-full">
                <span className="text-orange-700 font-semibold text-sm">Service</span>
              </div>
              <h1 data-testid="conception-page-title" className="text-4xl md:text-6xl font-bold mb-6 text-orange-600">
                {services.conception.title}
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-6">
                {services.conception.description}
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                {services.conception.details}
              </p>
            </div>

            {/* Vidéo à droite */}
            <div className="flex items-center justify-center">
              <SiteVideo name="conception" label="Conception paysagère Perinature" />
            </div>
          </div>
        </div>
      </section>

      {/* Details supprimée - déjà dans le hero */}
      <section className="py-0"></section>

      {/* Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
            Notre processus de conception
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.conception.features.map((feature, index) => (
              <div
                key={index}
                data-testid={`conception-feature-${index}`}
                className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-orange-100 hover:border-orange-300 transition-all duration-300 hover:shadow-lg"
              >
                <CheckCircle className="text-orange-500 flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
            Les étapes de votre projet
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">1. Rencontre et Écoute</h3>
              <p className="text-gray-600">
                Nous visitons votre terrain et discutons de vos envies, besoins et budget.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Ruler className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">2. Conception du Plan</h3>
              <p className="text-gray-600">
                Nous créons un plan détaillé avec choix des végétaux et matériaux adaptés.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">3. Validation du Projet</h3>
              <p className="text-gray-600">
                Présentation du projet final avec devis précis et planification des travaux.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {conceptionImages.length > 0 && (
        <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
              Exemples de conceptions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {conceptionImages.map((image) => (
                <div
                  key={image.id}
                  data-testid={`conception-gallery-item-${image.id}`}
                  className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <img
                    src={image.url}
                    data-testid={`conception-gallery-image-${image.id}`}
                    alt={image.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg">{image.title}</h3>
                      <p className="text-sm text-gray-200">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">
            Commencez votre projet de conception
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Parlons de votre projet. Nous vous accompagnons de l'idée à la réalisation.
          </p>
          <Link
            to="/contact"
            data-testid="conception-contact-link"
            className="inline-block px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-all duration-200 hover:scale-105 shadow-xl"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Conception;